import React, { useMemo } from 'react';
import {BaseComponent, Building, PhotoModel} from "../../types/protocols";
import "./BuildingImagesSection.css"
import {BaseComponentImpl} from "../../types/implementations";
import PhotoGallery from "../../Components/PhotoGallery";
import {translate} from "../../types/EnumTranslations";

interface BuildingSectionProps {
    title: string;
    label?: string;
    components?: { [key: string]: BaseComponent };
    building: Building;
    buildingKey: string;
    useDivider?: boolean;
    onImageClick?: (image: { photo: PhotoModel; componentName: string; source: string; year: string }) => void;
    selectedImageKey?: string;
}

const BuildingImagesSection: React.FC<BuildingSectionProps> = ({
                                                                   title,
                                                                   components,
                                                                   building,
                                                                   buildingKey,
                                                                   useDivider,
                                                                   onImageClick,
                                                                   selectedImageKey
                                                               }) => {
    // Group photos by year for each component
    const componentsWithPhotosByYear = useMemo(() => {
        if (!components) return [];
        
        const componentImpls = Object.values(components).map(item => new BaseComponentImpl(item));
        
        if (componentImpls.length === 0) {
            return [];
        }

        // Check if any component has photos
        const hasAnyPhotos = componentImpls.some(item =>
            item.photos && Object.values(item.photos).length > 0
        );

        if (!hasAnyPhotos) {
            return [];
        }
        return componentImpls.map(component => {
            if (!component.photos || Object.values(component.photos).length === 0) {
                return { component, photosByYear: {} };
            }

            const photos = Object.values(component.photos);
            
            // Sort photos by date (newest first)
            photos.sort((a, b) => {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return dateB.getTime() - dateA.getTime();
            });

            // Group by year
            const photosByYear: { [year: string]: PhotoModel[] } = {};
            photos.forEach(photo => {
                const year = new Date(photo.date).getFullYear().toString();
                if (!photosByYear[year]) {
                    photosByYear[year] = [];
                }
                photosByYear[year].push(photo);
            });

            return { component, photosByYear };
        }).filter(item => Object.keys(item.photosByYear).length > 0);
    }, [components]);

    // Early returns after hooks
    if (!components) return null;
    if (componentsWithPhotosByYear.length === 0) return <></>;

    return (
        <div>
            {useDivider && <div style={{
                height: '2px',
                marginBottom: '35px',
                marginTop: '5px',
                backgroundColor: "#dddddd"
            }} />}
            <h3 style={{ textAlign: 'left', marginBottom: '20px' }}>{title}</h3>
            <div style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: "15px",
                padding: useDivider ? "0px" : "30px",
                gap: '40px',
                backgroundColor: useDivider ? "transparent" : "#fff",
                textAlign: "left",
                boxShadow: useDivider ? '' : '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}>
                {componentsWithPhotosByYear.map(({ component, photosByYear }, index) => (
                    <div key={index}>
                        <h5>{component.displayName(building)}</h5>
                        
                        {Object.keys(photosByYear)
                            .sort((a, b) => parseInt(b) - parseInt(a)) // Sort years descending (newest first)
                            .map(year => (
                                <div key={year} style={{ marginBottom: '20px' }}>
                                    <h6 style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: '#666',
                                        marginBottom: '10px',
                                        marginTop: '15px'
                                    }}>
                                        {year}
                                    </h6>
                                    <PhotoGallery
                                        photos={photosByYear[year].reduce((acc, photo) => {
                                            acc[photo.key] = photo;
                                            return acc;
                                        }, {} as { [key: string]: PhotoModel })}
                                        buildingKey={buildingKey}
                                        onImageClick={onImageClick ? (photo) => {
                                            // If this image is already selected, don't call onImageClick (let PhotoGallery handle FancyBox)
                                            if (selectedImageKey === photo.key) {
                                                return;
                                            }
                                            onImageClick({
                                                photo,
                                                componentName: component.displayName(building),
                                                source: title,
                                                year
                                            });
                                        } : undefined}
                                    />
                                </div>
                            ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default BuildingImagesSection;